import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, IconButton, Box, Typography, Button,
    TextField, MenuItem, Tooltip, Switch, Slide, Avatar, Chip, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import {
    getEmployeeSocieties,
    assignEmployeeSociety,
    changeEmployeeSocietyRole,
    bulkStatusEmployee,
    deleteEmployee,
} from '../../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction';
import { employeeFullName } from './employeeConstants';
import { useSocietyRoles, roleToOption } from './useSocietyRoles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 0 },
};

const rowActive = (s) => s?.is_active === 1 || s?.is_active === true;

/**
 * View-details panel: manage all communities a person belongs to.
 * Backed by GET /employees/:id/societies; supports assign-to-community,
 * change-role, activate/deactivate and delete per membership.
 */
const EmployeeSocietiesModal = ({ open, person, societyOptions = [], typeOptions = [], onClose, onChanged, onEditRecord }) => {
    const dispatch = useDispatch();
    const {
        societiesData, societiesLoading, assignLoading, changeRoleLoading, bulkLoading, deleteLoading,
    } = useSelector((state) => state.CtrlEmployeeReducer || {});

    const personId = person?.employee_id;

    const [showAssign, setShowAssign] = useState(false);
    const [assign, setAssign] = useState({ society_id: '', employee_type_id: '', role_id: '' });
    const [editingSociety, setEditingSociety] = useState(null); // { societyId, roleId }

    // Independent role pickers → no shared-state clobber.
    const { roles: assignRoles, loading: assignRolesLoading } = useSocietyRoles(showAssign ? assign.society_id : '');
    const { roles: editRoles, loading: editRolesLoading } = useSocietyRoles(editingSociety?.societyId || '');

    const refetch = () => { if (personId) dispatch(getEmployeeSocieties({ id: personId })); };

    useEffect(() => {
        if (open && personId) dispatch(getEmployeeSocieties({ id: personId }));
        if (open) { setShowAssign(false); setAssign({ society_id: '', employee_type_id: '', role_id: '' }); setEditingSociety(null); }
    }, [open, personId, dispatch]);

    const societies = societiesData?.societies || [];

    const afterChange = () => { refetch(); onChanged?.(); };

    const handleAssign = () => {
        if (!assign.society_id || !assign.employee_type_id) return;
        dispatch(assignEmployeeSociety({
            id: personId,
            society_id: assign.society_id,
            employee_type_id: assign.employee_type_id,
            role_id: assign.role_id || undefined,
            onSuccess: () => {
                setShowAssign(false);
                setAssign({ society_id: '', employee_type_id: '', role_id: '' });
                afterChange();
            },
        }));
    };

    const handleSaveRole = () => {
        if (!editingSociety?.roleId) return;
        dispatch(changeEmployeeSocietyRole({
            id: personId,
            societyId: editingSociety.societyId,
            role_id: editingSociety.roleId,
            onSuccess: () => { setEditingSociety(null); onChanged?.(); },
        }));
    };

    const handleToggle = (s) => {
        dispatch(bulkStatusEmployee({
            employee_ids: [s.employee_id],
            is_active: !rowActive(s),
            onSuccess: afterChange,
        }));
    };

    const handleDelete = (s) => {
        dispatch(deleteEmployee({ id: s.employee_id, onSuccess: afterChange }));
    };

    const busy = assignLoading || changeRoleLoading || bulkLoading || deleteLoading;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} TransitionComponent={Transition}
            maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            {/* Header */}
            <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef1f6' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                    <Avatar sx={{ width: 44, height: 44, bgcolor: '#487fff', fontWeight: 700 }}>
                        {employeeFullName(person).charAt(0).toUpperCase() || 'E'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                            {employeeFullName(person)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                            {person?.email || '—'}{person?.phone ? ` · ${person.phone}` : ''}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} disabled={busy} size="small"
                    sx={{ color: '#64748b', bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b' }}>
                        Communities {Array.isArray(societies) ? `(${societies.length})` : ''}
                    </Typography>
                    <Button size="small" variant={showAssign ? 'text' : 'outlined'}
                        onClick={() => setShowAssign((v) => !v)}
                        startIcon={<Icon icon={showAssign ? 'solar:close-circle-linear' : 'solar:add-circle-linear'} style={{ fontSize: 16 }} />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                        {showAssign ? 'Cancel' : 'Add to Community'}
                    </Button>
                </Box>

                {/* Assign to community */}
                {showAssign && (
                    <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e5e9f0' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                            <TextField select label="Community" size="small" required sx={fieldSx}
                                value={assign.society_id}
                                onChange={(e) => setAssign((p) => ({ ...p, society_id: e.target.value, role_id: '' }))}>
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {societyOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                            <TextField select label="Employee Type" size="small" required sx={fieldSx}
                                value={assign.employee_type_id}
                                onChange={(e) => setAssign((p) => ({ ...p, employee_type_id: e.target.value }))}>
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {typeOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                            <TextField select label="Access Role" size="small" sx={fieldSx}
                                value={assign.role_id}
                                onChange={(e) => setAssign((p) => ({ ...p, role_id: e.target.value }))}
                                disabled={!assign.society_id || assignRolesLoading}
                                helperText={assignRolesLoading ? 'Loading…' : 'Optional'}>
                                <MenuItem value=""><em>Default (from type)</em></MenuItem>
                                {assignRoles.map(roleToOption).map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <Button variant="contained" disableElevation size="small"
                                onClick={handleAssign}
                                disabled={!assign.society_id || !assign.employee_type_id || assignLoading}
                                startIcon={assignLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Icon icon="solar:add-circle-bold" style={{ fontSize: 16 }} />}
                                sx={{ bgcolor: '#487fff', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                                Add
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Societies table */}
                <Box sx={{ border: '1px solid #eef1f6', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                    <th style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Community</th>
                                    <th style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Type</th>
                                    <th style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Access Role</th>
                                    <th style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Status</th>
                                    <th style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700, textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {societiesLoading ? (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>
                                        <CircularProgress size={20} />
                                    </td></tr>
                                ) : societies.length === 0 ? (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>No communities</td></tr>
                                ) : (
                                    societies.map((s) => {
                                        const active = rowActive(s);
                                        const isEditing = editingSociety?.societyId === s.society?.society_id;
                                        return (
                                            <tr key={s.employee_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>{s.society?.name || '—'}</td>
                                                <td style={{ padding: '10px 12px', color: '#475569' }}>{s.employee_type?.type || '—'}</td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    {isEditing ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField select size="small" sx={{ ...fieldSx, minWidth: 150 }}
                                                                value={editingSociety.roleId}
                                                                onChange={(e) => setEditingSociety((p) => ({ ...p, roleId: e.target.value }))}
                                                                disabled={editRolesLoading}>
                                                                <MenuItem value=""><em>{editRolesLoading ? 'Loading…' : 'Select role'}</em></MenuItem>
                                                                {editRoles.map(roleToOption).map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                                                            </TextField>
                                                            <IconButton size="small" color="primary" onClick={handleSaveRole} disabled={!editingSociety.roleId || changeRoleLoading}>
                                                                <Icon icon="solar:check-circle-bold" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => setEditingSociety(null)}>
                                                                <Icon icon="solar:close-circle-linear" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            {s.role?.role_name
                                                                ? <Chip size="small" label={s.role.role_name} sx={{ height: 22, fontSize: 11, bgcolor: '#eef4ff', color: '#3f6ede' }} />
                                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                                            <Tooltip title="Change role" arrow>
                                                                <IconButton size="small"
                                                                    onClick={() => setEditingSociety({ societyId: s.society?.society_id, roleId: s.role?.role_id || '' })}>
                                                                    <Icon icon="solar:pen-linear" style={{ fontSize: 15, color: '#64748b' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <Tooltip title={active ? 'Active' : 'Inactive'} arrow>
                                                        <Switch checked={active} onChange={() => handleToggle(s)} size="small" disabled={bulkLoading} />
                                                    </Tooltip>
                                                </td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {onEditRecord && (
                                                        <Tooltip title="Edit details" arrow>
                                                            <IconButton size="small" onClick={() => onEditRecord(s)}>
                                                                <Icon icon="solar:pen-2-linear" style={{ fontSize: 16, color: '#059669' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Remove from community" arrow>
                                                        <IconButton size="small" onClick={() => handleDelete(s)} disabled={deleteLoading}>
                                                            <Icon icon="solar:trash-bin-trash-linear" style={{ fontSize: 16, color: '#dc2626' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: '#94a3b8' }}>
                    <Icon icon="solar:info-circle-linear" style={{ fontSize: 14, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 11.5 }}>
                        Changing an access role updates the login's RBAC membership for that community only.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eef1f6', bgcolor: '#fbfcfe' }}>
                <Button onClick={onClose} disabled={busy} variant="text"
                    sx={{ color: '#475569', fontWeight: 600, textTransform: 'none', px: 2 }}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeSocietiesModal;
